import {
  CAPTION_SENTENCE_PAUSE_SECONDS,
  CAPTION_SYLLABLES_PER_SECOND_SEED,
  captionInternalPauseCount,
  captionSyllableCount,
  scanCaptionText,
} from "@/lib/voice-chat/caption-pacing"
import { voiceClockNow } from "@/lib/voice-chat/voice-chat-utils"
import { voiceTracer } from "@/lib/voice-trace/voice-trace"

const ASSISTANT_CAPTION_MIN_AUDIO_SECONDS = 0.25
const ASSISTANT_CAPTION_GLOBAL_LANGUAGE = "__global__"
// Per-language seed priors. The unit estimator counts CJK chars ~1:1, and
// CJK speech runs ~6–7 estimator units/s vs the Latin ~4.5 — seeding a ja/zh
// bucket at 4.5 starts every first turn ~30% slow, the exact cold-start lag
// class the closed loop was built to escape. Keyed by primary subtag
// (CLD reports e.g. "zh-Hant").
const ASSISTANT_CAPTION_SEED_BY_LANGUAGE: Record<string, number> = {
  ja: 6.5,
  ko: 6.5,
  zh: 6.5,
}

function seedRateForLanguage(language: string) {
  const primary = language.split(/[-_]/)[0]
  return (
    ASSISTANT_CAPTION_SEED_BY_LANGUAGE[primary] ??
    CAPTION_SYLLABLES_PER_SECOND_SEED
  )
}
// The seed's weight in the duration-weighted bucket, as pseudo-seconds of
// prior evidence. Short turns are the least trustworthy samples (a fixed
// ~0.3-0.5s of non-speech overhead — start-anchor skew, trailing buffer
// drain — sits in their denominator), so thin evidence must not yank the
// pacer off the seed: voice-trace-7369181's 4.5s first turn measured 4.23,
// and pacing the following 45s turn with it landed 11 syllables behind.
const ASSISTANT_CAPTION_RATE_PRIOR_SECONDS = 10

const captionsTrace = voiceTracer("captions")

interface CaptionRateMeterDeps {
  onCaptionRateMeasured(syllablesPerSecond: number): void
  snapshot(): { selectedSpeed: number }
}

export interface MeteredAssistantItem {
  itemId: string
  text: string
  // Pre-snap reveal position when the buffer stopped — how far the pacer got
  // vs the audio; the `caption_sync_landing` tuning metric.
  revealedLength: number
}

// Speaking time accumulated by the engine's speech-gate loop over the window,
// null when the gate was unavailable at window start (unmetered browser) —
// those windows measure the ungated way, matching the ungated reveal clock.
export interface MeteredSpeakingTime {
  seconds: number
}

interface CaptionRateSample {
  units: number
  audioSeconds: number
  speakingSeconds: number | null
  // Speech seconds normalized to playback speed 1.0 — the bucket denominator.
  speechSeconds: number
  rate: number
}

// Only one audio buffer plays at a time, so the meter tracks a single open
// window rather than keying by response id: in a gapless handoff
// (tool filler → narration sharing one buffer) `output_audio_buffer.started`
// carries the FIRST response's id while `.stopped` carries the LAST's, and a
// keyed lookup missed exactly those multi-response windows.
interface OpenCaptionWindow {
  responseId: string
  startedAt: number
}

/**
 * Learns duration-weighted voice/language caption rates from RTC physical audio
 * windows. Audio-stop samples and persistence language detection can arrive in
 * either order and converge by provider item id; one window may span gapless
 * multi-response playback, so response identity cannot delimit a measurement.
 */
export class CaptionRateMeter {
  private openWindow: OpenCaptionWindow | null = null
  private readonly pendingRateByItem = new Map<string, CaptionRateSample>()
  // Persist usually completes BEFORE the audio buffer drains (generation
  // outpaces playback), so the detected language often arrives while the
  // rate sample doesn't exist yet. Whichever side lands second folds the
  // sample into the bucket.
  private readonly languageByItem = new Map<string, string | null>()
  // Duration-weighted buckets: cumulative units and speed-normalized speech
  // seconds per language, so the rate is Σunits / Σseconds — a 45s turn
  // outweighs a noisy 4.5s one instead of averaging in equally. Seconds are
  // normalized to playback speed 1.0 so a mid-session speed change doesn't
  // pollute the buckets.
  private readonly unitsByLanguage = new Map<string, number>()
  private readonly speechSecondsByLanguage = new Map<string, number>()
  private captionRate = CAPTION_SYLLABLES_PER_SECOND_SEED

  constructor(private deps: CaptionRateMeterDeps) {
    // Measured samples multiply by the playback speed (recordSample below);
    // the seed must too, or a 1.5×-speed session paces its whole first
    // window ~33% slow. Emit only when speed ≠ 1: the slice's rate survives
    // between same-tab sessions, and at speed 1 the stale-but-learned value
    // beats resetting to the seed.
    const { selectedSpeed } = deps.snapshot()
    const speed = selectedSpeed > 0 ? selectedSpeed : 1
    if (speed !== 1) {
      this.captionRate = CAPTION_SYLLABLES_PER_SECOND_SEED * speed
      this.deps.onCaptionRateMeasured(this.captionRate)
    }
  }

  get captionSyllablesPerSecond() {
    return this.captionRate
  }

  audioStarted(responseId: string) {
    this.openWindow = { responseId, startedAt: voiceClockNow() }
  }

  audioStopped(
    responseId: string,
    assistantItems: MeteredAssistantItem[],
    speaking: MeteredSpeakingTime | null = null
  ) {
    const window = this.openWindow
    this.openWindow = null
    if (!window) return

    const audioSeconds = (voiceClockNow() - window.startedAt) / 1000
    const queuedChars = assistantItems.reduce(
      (queued, item) =>
        queued + Math.max(0, item.text.length - item.revealedLength),
      0
    )
    const queuedSyllables = assistantItems.reduce(
      (queued, item) =>
        queued +
        Math.max(
          0,
          captionSyllableCount(item.text) -
            captionSyllableCount(item.text.slice(0, item.revealedLength))
        ),
      0
    )
    // How the caption landed against the real audio end: queued > 0 means
    // the reveal ran behind (snap covers the rest); queued === 0 means it
    // finished early by some unmeasured margin.
    captionsTrace.info("caption_sync_landing", {
      responseId,
      startResponseId: window.responseId,
      itemCount: assistantItems.length,
      audioSeconds,
      speakingSeconds: speaking ? speaking.seconds : null,
      queuedChars,
      queuedSyllables,
    })
    if (audioSeconds < ASSISTANT_CAPTION_MIN_AUDIO_SECONDS) return

    const items = assistantItems.filter((item) => item.text.length > 0)
    const totalUnits = items.reduce(
      (units, item) => units + captionSyllableCount(item.text),
      0
    )
    if (totalUnits === 0) return

    const { selectedSpeed } = this.deps.snapshot()
    const speed = selectedSpeed > 0 ? selectedSpeed : 1
    const speechSeconds = speaking
      ? // Gated window: silence was measured, not estimated. The floor guards
        // against a malfunctioning level feed producing a near-zero speaking
        // time and a runaway rate; the ceiling is physical.
        Math.min(Math.max(speaking.seconds, audioSeconds / 2), audioSeconds)
      : // Ungated: back the voice's estimated sentence pauses out of the wall
        // clock, since the ungated scheduler debits the same boundaries at
        // reveal time — measuring against the full window would subtract them
        // twice and pace every turn systematically slow (voice-trace-7369179:
        // measured 3.93 vs ~4.6 speech-only).
        Math.max(
          audioSeconds -
            this.windowPauseCount(items) * CAPTION_SENTENCE_PAUSE_SECONDS,
          audioSeconds / 2
        )

    // One sample for the whole window: every item played inside the same
    // measured seconds, so per-item rates would each divide a fraction of the
    // units by the full window. The sample folds into the language bucket of
    // the window's dominant item (most units) — a filler + narration window is
    // overwhelmingly the narration's language.
    const dominant = items.reduce((left, right) =>
      captionSyllableCount(right.text) > captionSyllableCount(left.text)
        ? right
        : left
    )
    const sample: CaptionRateSample = {
      units: totalUnits,
      audioSeconds,
      speakingSeconds: speaking ? speaking.seconds : null,
      speechSeconds: speechSeconds * speed,
      rate: totalUnits / speechSeconds / speed,
    }
    if (this.languageByItem.has(dominant.itemId)) {
      const language = this.languageByItem.get(dominant.itemId) ?? null
      this.languageByItem.delete(dominant.itemId)
      this.recordSample(language, sample)
    } else {
      this.pendingRateByItem.set(dominant.itemId, sample)
    }
  }

  // Sentence pauses the voice plays within the window's audio. A non-final
  // item's trailing boundary DOES play (the next item's audio follows it), so
  // only the last item excludes its trailing one.
  private windowPauseCount(items: MeteredAssistantItem[]) {
    return items.reduce((pauses, item, index) => {
      if (index === items.length - 1) {
        return pauses + captionInternalPauseCount(item.text)
      }
      const scan = scanCaptionText(item.text)
      return pauses + scan.cumulativePauses[item.text.length]
    }, 0)
  }

  // Barge-in: the buffer was cleared, not played out — no `audioStopped` will
  // come for this window, so drop it instead of leaking it until session end.
  // (No landing trace or rate sample: the audio didn't finish, so neither is
  // meaningful.)
  audioCleared() {
    this.openWindow = null
  }

  turnPersisted(itemId: string, detectedLanguage: string | null) {
    const pendingRate = this.pendingRateByItem.get(itemId)
    if (!pendingRate) {
      // The audio is still playing; audioStopped folds the sample later.
      this.languageByItem.set(itemId, detectedLanguage)
      return
    }

    this.pendingRateByItem.delete(itemId)
    this.recordSample(detectedLanguage, pendingRate)
  }

  private recordSample(
    detectedLanguage: string | null,
    sample: CaptionRateSample
  ) {
    const language = detectedLanguage ?? ASSISTANT_CAPTION_GLOBAL_LANGUAGE
    const priorUnits =
      this.unitsByLanguage.get(language) ??
      seedRateForLanguage(language) * ASSISTANT_CAPTION_RATE_PRIOR_SECONDS
    const priorSeconds =
      this.speechSecondsByLanguage.get(language) ??
      ASSISTANT_CAPTION_RATE_PRIOR_SECONDS
    const nextUnits = priorUnits + sample.units
    const nextSeconds = priorSeconds + sample.speechSeconds
    const nextRate = nextUnits / nextSeconds
    this.unitsByLanguage.set(language, nextUnits)
    this.speechSecondsByLanguage.set(language, nextSeconds)
    const currentState = this.deps.snapshot()
    this.captionRate = nextRate * currentState.selectedSpeed
    this.deps.onCaptionRateMeasured(this.captionRate)
    captionsTrace.info("assistant_caption_rate_measured", {
      units: sample.units,
      audioSeconds: sample.audioSeconds,
      speakingSeconds: sample.speakingSeconds,
      syllablesPerSecond: sample.rate,
      bucketRate: nextRate,
      speed: currentState.selectedSpeed,
      language: detectedLanguage,
    })
  }

  sessionStopped() {
    this.openWindow = null
    this.languageByItem.clear()
    this.pendingRateByItem.clear()
  }
}
